import { ICoreContext } from '@msdyn365-commerce/core';
import { getCartState, GetCheckoutCartInput } from '@msdyn365-commerce/global-state';
import { AttributeValueBase, IContext, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IAllAddresses, IResponseResult } from '../types/buybox';
import { PropertyType } from '../propertyTypeEnum';
import getAttributeObject from './get-address-attribute-object';
import { updateCartLinesAsync, updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { AppealType } from './lcsab-form';

export interface IAllAddressesWithDocs extends IAllAddresses {
    documentList?: IResponseResult[];
}

const getAttributes = (
    data: {
        address: IAllAddressesWithDocs;
        document?: IResponseResult;
    },
    context: ICoreContext
): AttributeValueBase[] => {
    const {
        customerFirstName,
        customerLastName,
        customerEmail,
        customerEmailConfirm,
        customerAddress,
        customerAddress2,
        customerCity,
        customerProvince,
        customerPostalCode,
        customerResidentialPhone,
        customerCountry,
        siteMunicipalAddressOfSite,
        siteOtherDocuments,
        appelantFirstName,
        appelantLastName,
        appelantAgentName,
        appelantEmailAddress,
        appelantEmailConfirmAddress,
        appelantAddress,
        appelantAddress2,
        appelantCity,
        appelantProvince,
        appelantPostalCode,
        appelantResidentialPhone,
        appealDevelopmentPermitType,
        appealReasonForAppeal,
        appealEstimatedPresentationTime,
        appealUsingAgent,
        appealAnticipateIssues,
        appealReason,
        appealBringingWitness,
        appealNumberOfWitness,
        customerInfoDPN,
        customerInfoSPN,
        appellantInfoDPN,
        appellantInfoSPN,
        usingAgentInfo,
        lcsabAgentName,
        lcsabAgentEmailAddress,
        lcsabAgentAddress1,
        lcsabAgentAddress2,
        lcsabAgentCity,
        lcsabAgentProvince,
        lcsabAgentPostalCode,
        lcsabAgentDPN,
        lcsabAgentSPN,
        lcsabAgentDecisionDate,
        lcsabAppealType
    } = context?.request.app.config;
    const { searchKeyNameAttribute, searchKeyValueAttribute, addressAttribute, documentAttribute } = context?.request.app.config;

    const attributeArr: AttributeValueBase[] = [];

    const { address, document } = data;

    switch (address.propertyType) {
        case PropertyType.address:
            // rules to set the attribute
            // 1. sequence should be: Suite Number StreetName StreetType Quadrant.
            // if suite is defined then it set the suite value in attribute.
            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            if (address.serviceAddress.suite) {
                attributeArr.push(
                    getAttributeObject(
                        searchKeyValueAttribute,
                        `${address.serviceAddress?.suite} || ${address.serviceAddress.number} || ${address.serviceAddress.streetName} || ${address.serviceAddress.streetType} || ${address.serviceAddress.quadrant}`
                    )
                );
            } else {
                attributeArr.push(
                    getAttributeObject(
                        searchKeyValueAttribute,
                        `${address.serviceAddress.number} || ${address.serviceAddress.streetName} || ${address.serviceAddress.streetType} || ${address.serviceAddress.quadrant}`
                    )
                );
            }
            break;

        case PropertyType.legalDescription:
            // rules to set the attribute
            // 1. sequence should be: Plan || Block || Lot
            // if Block and LOt is defined then it set the Block and Lot value in attribute
            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            if (address.legalDescriptionVerified?.block && address.legalDescriptionVerified?.lot) {
                attributeArr.push(
                    getAttributeObject(
                        searchKeyValueAttribute,
                        `${address.legalDescriptionVerified.plan} || ${address.legalDescriptionVerified?.block} || ${address.legalDescriptionVerified?.lot}`
                    )
                );
            } else if (address.legalDescriptionVerified?.block) {
                attributeArr.push(
                    getAttributeObject(
                        searchKeyValueAttribute,
                        `${address.legalDescriptionVerified.plan} || ${address.legalDescriptionVerified?.block} || `
                    )
                );
            } else if (address.legalDescriptionVerified?.lot) {
                attributeArr.push(
                    getAttributeObject(
                        searchKeyValueAttribute,
                        `${address.legalDescriptionVerified.plan} ||  || ${address.legalDescriptionVerified?.lot}`
                    )
                );
            } else {
                attributeArr.push(getAttributeObject(searchKeyValueAttribute, `${address.legalDescriptionVerified.plan} ||  || `));
            }
            break;

        case PropertyType.legalDescriptionCondo:
            // rules to set the attribute
            // 1. sequence should be: Plan || Unit
            // if unit is defined then it set the Unit value in attribute
            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            if (address.legalDescriptionCondoVerified?.unit) {
                attributeArr.push(
                    getAttributeObject(
                        searchKeyValueAttribute,
                        `${address.legalDescriptionCondoVerified.plan} || ${address.legalDescriptionCondoVerified?.unit}`
                    )
                );
            } else {
                attributeArr.push(getAttributeObject(searchKeyValueAttribute, `${address.legalDescriptionCondoVerified.plan}`));
            }
            break;

        case PropertyType.sdab:
            const { customerInformation, appelantInformation, appealAgainst, anticipateIssues, witness } = address.sdab;
            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            attributeArr.push(getAttributeObject(customerFirstName, `${customerInformation?.firstName.value!}`));
            attributeArr.push(getAttributeObject(customerLastName, `${customerInformation?.lastName.value!}`));
            attributeArr.push(getAttributeObject(customerEmail, `${customerInformation?.email.value!}`));
            attributeArr.push(getAttributeObject(customerEmailConfirm, `${customerInformation?.emailConfirm.value!}`));
            attributeArr.push(getAttributeObject(customerAddress, `${customerInformation?.address1.value!}`));
            attributeArr.push(getAttributeObject(customerAddress2, `${customerInformation?.address2.value!}`));
            attributeArr.push(getAttributeObject(customerCity, `${customerInformation?.city.value!}`));
            attributeArr.push(getAttributeObject(customerProvince, `${customerInformation?.province.value!}`));
            attributeArr.push(getAttributeObject(customerPostalCode, `${customerInformation?.postalCode.value!}`));
            attributeArr.push(getAttributeObject(customerResidentialPhone, `${customerInformation?.residentialPhone.value!}`));
            attributeArr.push(getAttributeObject(customerCountry, `${customerInformation?.country.value!}`));
            attributeArr.push(getAttributeObject(siteMunicipalAddressOfSite, `${customerInformation?.municipalAddress.value!}`));
            attributeArr.push(getAttributeObject(siteOtherDocuments, `${customerInformation?.otherDocuments.value!}`));
            attributeArr.push(getAttributeObject(appelantFirstName, `${appelantInformation?.firstName.value!}`));
            attributeArr.push(getAttributeObject(appelantLastName, `${appelantInformation?.lastName.value!}`));
            attributeArr.push(getAttributeObject(appelantAgentName, `${appelantInformation?.agentName.value!}`));
            attributeArr.push(getAttributeObject(appelantEmailAddress, `${appelantInformation?.email.value!}`));
            attributeArr.push(getAttributeObject(appelantEmailConfirmAddress, `${appelantInformation?.emailConfirm.value!}`));
            attributeArr.push(getAttributeObject(appelantAddress, `${appelantInformation?.address1.value!}`));
            attributeArr.push(getAttributeObject(appelantAddress2, `${appelantInformation?.address2.value!}`));
            attributeArr.push(getAttributeObject(appelantCity, `${appelantInformation?.city.value!}`));
            attributeArr.push(getAttributeObject(appelantProvince, `${appelantInformation?.province.value!}`));
            attributeArr.push(getAttributeObject(appelantPostalCode, `${appelantInformation?.postalCode.value!}`));
            attributeArr.push(getAttributeObject(appelantResidentialPhone, `${appelantInformation?.residentialPhone.value!}`));
            attributeArr.push(
                getAttributeObject(
                    appealDevelopmentPermitType,
                    `${appealAgainst?.appeal.AppealAgainstDevelopment} || ${appealAgainst?.appeal.AppealAgainstSubDivisionApplication} || ${appealAgainst?.appeal.AppealAgainstNoticOrder}`
                )
            );
            attributeArr.push(getAttributeObject(appealReasonForAppeal, `${appealAgainst?.reasonOfAppeal.value!}`));
            attributeArr.push(getAttributeObject(appealEstimatedPresentationTime, `${appealAgainst?.presentationTime.value!}`));
            attributeArr.push(getAttributeObject(appealUsingAgent, `${appealAgainst?.usingAgentOrLegalCounsel.value!}`));
            attributeArr.push(getAttributeObject(appealAnticipateIssues, `${anticipateIssues?.isAnticipateIssuesWithYourAppeal.value!}`));
            attributeArr.push(getAttributeObject(appealReason, `${anticipateIssues?.isAnticipateIssuesWithYourAppealDescription.value!}`));
            attributeArr.push(getAttributeObject(appealBringingWitness, `${witness?.bringingWitness.value!}`));
            attributeArr.push(getAttributeObject(appealNumberOfWitness, `${witness?.howManyWitness.value!}`));
            break;

        case PropertyType.lcsab:
            const {
                customerInformation: customerInfo,
                appellantInformation,
                usingAgent,
                agentInformation,
                petAppealType,
                categoryAppealType,
                licenseType,
                typeOfLicense,
                remedialType,
                dateDecisionAndReasonForAppeal
            } = address.lcsab;

            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            // Customer Information Section
            attributeArr.push(getAttributeObject(customerFirstName, `${customerInfo?.firstName.value!}`));
            attributeArr.push(getAttributeObject(customerLastName, `${customerInfo?.lastName.value!}`));
            attributeArr.push(getAttributeObject(customerEmail, `${customerInfo?.email.value!}`));
            attributeArr.push(getAttributeObject(customerEmailConfirm, `${customerInfo?.emailConfirm.value!}`));
            attributeArr.push(getAttributeObject(customerAddress, `${customerInfo?.address1.value!}`));
            attributeArr.push(getAttributeObject(customerAddress2, `${customerInfo?.address2.value!}`));
            attributeArr.push(getAttributeObject(customerCity, `${customerInfo?.city.value!}`));
            attributeArr.push(getAttributeObject(customerProvince, `${customerInfo?.province.value!}`));
            attributeArr.push(getAttributeObject(customerPostalCode, `${customerInfo?.postalCode.value!}`));
            attributeArr.push(getAttributeObject(customerInfoDPN, `${customerInfo?.dayTimePhoneNumber.value!}`));
            attributeArr.push(getAttributeObject(customerInfoSPN, `${customerInfo?.secondaryPhoneNumber.value!}`));
            attributeArr.push(getAttributeObject(siteMunicipalAddressOfSite, `${appellantInformation?.municipalAddress?.value!}`));
            // APpellant Information
            attributeArr.push(getAttributeObject(appelantFirstName, `${appellantInformation?.firstName.value!}`));
            attributeArr.push(getAttributeObject(appelantLastName, `${appellantInformation?.lastName.value!}`));
            attributeArr.push(getAttributeObject(appelantEmailAddress, `${appellantInformation?.email.value!}`));
            attributeArr.push(getAttributeObject(appelantEmailConfirmAddress, `${appellantInformation?.emailConfirm.value!}`));
            attributeArr.push(getAttributeObject(appelantAddress, `${appellantInformation?.address1.value!}`));
            attributeArr.push(getAttributeObject(appelantAddress2, `${appellantInformation?.address2.value!}`));
            attributeArr.push(getAttributeObject(appelantCity, `${appellantInformation?.city.value!}`));
            attributeArr.push(getAttributeObject(appelantProvince, `${appellantInformation?.province.value!}`));
            attributeArr.push(getAttributeObject(appelantPostalCode, `${appellantInformation?.postalCode.value!}`));
            attributeArr.push(getAttributeObject(appellantInfoDPN, `${appellantInformation?.dayTimePhoneNumber.value!}`));
            attributeArr.push(getAttributeObject(appellantInfoSPN, `${appellantInformation?.secondaryPhoneNumber.value!}`));
            // Agent Information
            attributeArr.push(getAttributeObject(usingAgentInfo, `${usingAgent}`));
            attributeArr.push(getAttributeObject(lcsabAgentName, `${agentInformation?.agentName.value}`));
            attributeArr.push(getAttributeObject(lcsabAgentEmailAddress, `${agentInformation?.email.value}`));
            attributeArr.push(getAttributeObject(lcsabAgentAddress1, `${agentInformation?.address1.value}`));
            attributeArr.push(getAttributeObject(lcsabAgentAddress2, `${agentInformation?.address2.value}`));
            attributeArr.push(getAttributeObject(lcsabAgentCity, `${agentInformation?.city.value}`));
            attributeArr.push(getAttributeObject(lcsabAgentProvince, `${agentInformation?.province.value}`));
            attributeArr.push(getAttributeObject(lcsabAgentPostalCode, `${agentInformation?.postalCode.value}`));
            attributeArr.push(getAttributeObject(lcsabAgentDPN, `${agentInformation?.dayTimePhoneNumber.value}`));
            attributeArr.push(getAttributeObject(lcsabAgentSPN, `${agentInformation?.secondaryPhoneNumber.value}`));
            if (categoryAppealType === AppealType.PetOwnershipAppeals) {
                attributeArr.push(getAttributeObject(lcsabAppealType, `${categoryAppealType} - ${petAppealType || ''}`));
            } else if (categoryAppealType === AppealType.RemedialOrder) {
                attributeArr.push(getAttributeObject(lcsabAppealType, `${categoryAppealType} - ${remedialType || ''}`));
            } else if (categoryAppealType === AppealType.LicensePermitDecisionOrNotice) {
                attributeArr.push(getAttributeObject(lcsabAppealType, `${categoryAppealType} - ${typeOfLicense} - ${licenseType}`));
            }
            attributeArr.push(getAttributeObject(lcsabAgentDecisionDate, `${dateDecisionAndReasonForAppeal?.date.value}`));
            attributeArr.push(getAttributeObject(appealReasonForAppeal, `${dateDecisionAndReasonForAppeal?.reasonForAppeal.value}`));
            break;

        case PropertyType.community:
            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            attributeArr.push(getAttributeObject(searchKeyValueAttribute, `${address.communityVerified?.communityName!}`));
            break;

        case PropertyType.quadrant:
            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            attributeArr.push(getAttributeObject(searchKeyValueAttribute, `${address.quadrantVerified?.quadrant!}`));
            break;

        case PropertyType.blockProfile:
            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            attributeArr.push(getAttributeObject(searchKeyValueAttribute, `${address.blockProfileVerified?.blockProfile!}`));
            break;

        case PropertyType.ats:
            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            attributeArr.push(getAttributeObject(searchKeyValueAttribute, `${address.atsVerified?.atsValue!}`));
            break;

        case PropertyType.cityWide:
        case PropertyType.none:
            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            attributeArr.push(getAttributeObject(searchKeyValueAttribute, ``));
            break;

        default:
            // default include CPID, Roll number, Linc Number and Title number.
            attributeArr.push(getAttributeObject(searchKeyNameAttribute, `${address.propertyType!}`));
            attributeArr.push(getAttributeObject(searchKeyValueAttribute, `${address.lookForVerified.lookFor!}`));
            break;
    }

    let addressAttributeValue: AttributeValueBase;
    // setting address line attribute
    // Set the COC Address Attribute with || charactar separator
    if (
        address.propertyType !== PropertyType.sdab &&
        address.propertyType !== PropertyType.lcsab &&
        address.propertyType !== PropertyType.community &&
        address.propertyType !== PropertyType.quadrant &&
        address.propertyType !== PropertyType.blockProfile &&
        address.propertyType !== PropertyType.ats &&
        address.propertyType !== PropertyType.none &&
        address.propertyType !== PropertyType.cityWide
    ) {
        if (address.serviceAddress.suite) {
            addressAttributeValue = getAttributeObject(
                addressAttribute,
                `${address.serviceAddress?.suite} || ${address.serviceAddress.number} || ${address.serviceAddress.streetName} || ${address.serviceAddress.streetType} || ${address.serviceAddress.quadrant}`
            );
            attributeArr.push(addressAttributeValue);
        } else {
            addressAttributeValue = getAttributeObject(
                addressAttribute,
                `${address.serviceAddress.number} || ${address.serviceAddress.streetName} || ${address.serviceAddress.streetType} || ${address.serviceAddress.quadrant}`
            );
            attributeArr.push(addressAttributeValue);
        }
    }
    if (
        address.propertyType === PropertyType.community ||
        address.propertyType === PropertyType.quadrant ||
        address.propertyType === PropertyType.blockProfile ||
        address.propertyType === PropertyType.ats ||
        address.propertyType === PropertyType.cityWide ||
        address.propertyType === PropertyType.none
    ) {
        addressAttributeValue = getAttributeObject(documentAttribute, `${document?.documentId} || ${document?.documentName}`);
        attributeArr.push(addressAttributeValue);
    }

    return attributeArr;
};

// const getDefaultCartLine = (product: SimpleProduct, context: ICoreContext) => {
//     const cartLine: CartLine = {
//         CatalogId: context.actionContext.requestContext.apiSettings.catalogId,
//         Description: product.Description,
//         EntryMethodTypeValue: 3,
//         ItemId: product.ItemId,
//         ProductId: product.RecordId,
//         Quantity: 1,
//         TrackingId: '',
//         UnitOfMeasureSymbol: product.DefaultUnitOfMeasure,
//         IsGiftCardLine: product.IsGiftCard,
//         Price: product.Price
//     };
//     return cartLine;
// };

const getCartLinesWithAttributes = async (
    data: {
        address: IAllAddressesWithDocs;
        product: SimpleProduct;
        document?: IResponseResult;
    }[],
    context: ICoreContext
) => {
    const cartLines = (await getCartState(context.actionContext)).cart.CartLines;
    // const cartLines: CartLine[] = [];

    // creating attributes
    data.forEach(current => {
        const { address, product, document } = current;
        const attributes = getAttributes({ address, document }, context);

        // const cartLine = getDefaultCartLine(product, context);
        // cartLine.AttributeValues = attributes;
        // cartLines.push(cartLine);

        const cartLine = cartLines?.find(line => line.ItemId === product.ItemId && !line.AttributeValues?.length);
        cartLine && (cartLine.AttributeValues = attributes);
    });
    return cartLines || [];
};

export default async (allAddresses: IAllAddressesWithDocs[], context: ICoreContext, errorOccured?: () => void): Promise<void> => {
    const productsToAdd = allAddresses
        .map((address) => {
            if (address.documentList) {
                return address.documentList.map(document => ({ address, product: address.relatedProduct[0], document }));
            } else {
                return address.relatedProduct.map(product => ({ address, product }));
            }
        })
        .flat();

    const cartState = await getCartState(context.actionContext);
    const result = await cartState.addProductsToCart(productsToAdd);
    if (result.status === 'FAILED') {
        errorOccured && errorOccured();
        return;
    }

    const cartLines = await getCartLinesWithAttributes(productsToAdd, context);
    const ctx: IContext = { callerContext: context.actionContext };
    /* adding new values to cart line */
    try {
        // const updatedCart = await addCartLinesAsync(
        //     { callerContext: context.actionContext },
        //     cartState.cart.Id,
        //     cartLines,
        //     cartState.cart.Version
        // );
        const updatedCart = await updateCartLinesAsync(ctx, cartState.cart.Id, cartLines);
        // after updating cart refreshing the cart
        await cartState.refreshCart({});
        /** updating cart with new cart line items */
        await updateAsync({ callerContext: context.actionContext }, updatedCart)
            .then((newCart: any) => {
                context.actionContext.update(new GetCheckoutCartInput(context.request.apiSettings), newCart);
            })
            .catch(error => {
                context.telemetry.error(`${error}`);
            });
    } catch (error) {
        context.telemetry.error(`Updated cartlines failed: ${error}`);
    }
};
